import type { FC } from 'react'
import {
  alpha,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Theme,
  Typography,
  SxProps,
  Tooltip,
  type ListItemButtonProps,
} from '@mui/material'
import { Link } from 'react-router-dom'

import { ChevronRight } from '@dhl/icons'

import { MenuItemWithSubItems } from './types.js'

type Props = {
  disableTab: boolean
  isExpanded: boolean
  menuItem: MenuItemWithSubItems
}

export const styles: Record<'menuItem' | 'activeMenuItem' | 'subHeader' | 'listItemText', SxProps<Theme>> = {
  menuItem: (theme) => ({
    borderLeft: '2px solid transparent',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.04)',
      color: theme.palette.primary.main,
      '&> div, svg': {
        color: theme.palette.primary.main,
      },
    },
  }),
  activeMenuItem: (theme) => ({
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    color: theme.palette.primary.main,
    '&> div, svg': {
      color: theme.palette.primary.main,
    },
    borderLeft: `2px solid ${theme.palette.primary.main}`,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.05),
    },
  }),
  listItemText: {
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
  },
  subHeader: (theme) => ({
    p: 2,
    pb: 1,
    textTransform: 'inherit',
    ...theme.typography.body2,
  }),
}

export const SideMenuListItem: FC<Props> = ({ disableTab, menuItem, isExpanded }) => {
  const { key, icon, active, label, link, app, onClick, disabled, subMenuItems, external, target } = menuItem
  const hasChildNodes = Boolean(subMenuItems?.length)
  const tabIndexProp = disableTab ? { tabIndex: -1 } : {}
  const props = onClick ? { onClick } : { to: link, component: Link, state: { app } }
  const externalProps = {
    href: link,
    target,
    rel: 'noopener noreferrer',
    onClick,
  } satisfies ListItemButtonProps<'a'>

  const linkProps = external ? externalProps : props

  return (
    <>
      {menuItem.subHeader && isExpanded && (
        <ListSubheader disableSticky sx={styles.subHeader}>
          {menuItem.subHeader}
        </ListSubheader>
      )}
      <Tooltip
        title={label}
        placement="right"
        PopperComponent={isExpanded ? () => null : undefined}
        TransitionProps={{ timeout: 500 }}
        arrow
      >
        <ListItemButton
          {...tabIndexProp}
          {...linkProps}
          id={menuItem.id}
          sx={active ? styles.activeMenuItem : styles.menuItem}
          // TODO: phase out data-test, instead use data-testid
          data-test={`side-navigation-item-${key}`}
          data-testid={`side-navigation-item-${key}`}
          disabled={disabled}
          disableRipple
        >
          <ListItemIcon>{icon}</ListItemIcon>
          <ListItemText
            primary={
              <Typography variant={active ? 'h5' : 'body1'} component="span">
                {label}
              </Typography>
            }
            disableTypography
            sx={styles.listItemText}
          />
          {hasChildNodes && <ChevronRight />}
        </ListItemButton>
      </Tooltip>
    </>
  )
}
